import moment, { Moment } from 'moment';
import { RELATIVE, SHORT } from 'src/constants/dates';
import { IPassengerShifts, IPickupDropOrder } from 'src/types/manualOrders/api.types';
import { SavedOrder, Slot } from './editTransaction';

/**
 * What the edit transaction (editTransaction.ts) needs to know about a passenger's orders, read from the
 * server's passengers-for-dates answer.
 */

const toRelativeDate = (value?: string): string => {
    if (!value) return '';
    const parsed = moment(value, [RELATIVE, SHORT, 'DD.MM.YYYY', 'DD/MM/YYYY', moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format(RELATIVE) : '';
};

const toSaved = (o: IPickupDropOrder, date: string, side: 'pickup' | 'drop'): SavedOrder => ({
    orderCode: String(o.orderCode),
    date,
    shiftTime: o.shiftTime,
    side,
    city: o.city ?? '',
    street: o.street ?? '',
    houseNum: o.houseNum ?? '',
});

/** Every order the passenger has, each with its relative date and side */
const ordersOfPassenger = (passenger?: IPassengerShifts): SavedOrder[] =>
    (passenger?.dates || []).flatMap((day) => [
        ...(day.pickupOrders || []).map((o) => toSaved(o, day.relativeDate, 'pickup')),
        ...(day.dropOrders || []).map((o) => toSaved(o, day.relativeDate, 'drop')),
    ]);

/**
 * The passenger's orders in a ws_MyWayStudio_Get_Passsengers_4_Dates answer. Throws when the answer is not the
 * expected object (the table guards against a text answer the same way), so the transaction never reads
 * "no orders" out of an answer it could not parse.
 */
const ordersFromPassengersAnswer = (data: unknown, passId: string): SavedOrder[] => {
    const passengers = (data as { passengers?: IPassengerShifts[] } | null)?.passengers;
    if (!data || typeof data !== 'object' || !Array.isArray(passengers)) throw new Error('Unreadable orders answer');
    return ordersOfPassenger(passengers.find((p) => String(p?.passId) === String(passId)));
};

/** The dates to read for an edit: from the earliest to the latest of the edited day and the days being saved */
const readRange = (editDate: Date | Moment | string, dates: { relativeDate: string }[]) => {
    const all = [moment(editDate).format(RELATIVE), ...dates.map((d) => toRelativeDate(d.relativeDate))]
        .filter(Boolean)
        .sort();
    return { fromDate: all[0], toDate: all[all.length - 1] };
};

/**
 * Whether an order belongs to the edited shift: on the edited day, the pickup at its pickup time or the drop at
 * its drop time - only the sides the edit replaces. By side and time, never by the position of a code in the
 * shift cell (a drop-only cell holds the drop code first).
 */
const isOfEditedShift = (
    editDate: Date | Moment | string,
    replaced: { pickupTime?: string; dropTime?: string }
) => {
    const day = moment(editDate).format(RELATIVE);
    return (o: SavedOrder) =>
        o.date === day &&
        ((o.side === 'pickup' && !!replaced.pickupTime && o.shiftTime === replaced.pickupTime) ||
            (o.side === 'drop' && !!replaced.dropTime && o.shiftTime === replaced.dropTime));
};

/** The slots a save writes: each day it saves, each side it sends, at that side's time */
const targetSlots = (dates: { relativeDate: string }[], sides: { pickupTime?: string; dropTime?: string }): Slot[] =>
    dates
        .map((d) => toRelativeDate(d.relativeDate))
        .filter(Boolean)
        .flatMap((date) => [
            ...(sides.pickupTime ? [{ date, side: 'pickup' as const, shiftTime: sides.pickupTime }] : []),
            ...(sides.dropTime ? [{ date, side: 'drop' as const, shiftTime: sides.dropTime }] : []),
        ]);

/**
 * The shift id to save an old order again with (rollback). The read answer carries no shift id, and the server
 * refuses a save without a valid one (fn_MyWayStudio_Set_Shift_Order_by_Date answers 3 and inserts nothing); it
 * only validates the id, the order keeps its own time. So: the account's shift with that time on that side, else
 * the id of the shift being saved now.
 */
const shiftIdForOrder = (
    order: SavedOrder,
    shifts: { shiftId: number; pickupTime?: string; dropTime?: string }[],
    fallbackId?: string | number | null
): string | undefined => {
    const sameTime = shifts.find((s) =>
        order.side === 'pickup' ? s.pickupTime === order.shiftTime : s.dropTime === order.shiftTime
    );
    if (sameTime) return String(sameTime.shiftId);
    if (fallbackId !== undefined && fallbackId !== null && String(fallbackId) !== '') return String(fallbackId);
    return undefined;
};

/** The pickup and drop times a delete call names for these orders of one date */
const timesOf = (orders: SavedOrder[]) => ({
    pickupTime: orders.find((o) => o.side === 'pickup')?.shiftTime || '',
    dropTime: orders.find((o) => o.side === 'drop')?.shiftTime || '',
});

const editData = {
    ordersOfPassenger,
    ordersFromPassengersAnswer,
    readRange,
    isOfEditedShift,
    targetSlots,
    toRelativeDate,
    timesOf,
    shiftIdForOrder,
};

export default editData;
